import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import useAssessments from '@/data/hook/evaluation/useAssessments';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { Assessment } from '@/models/Assessment';
import CoursePath from '@/models/CoursePath';
import Evaluation from '@/models/Evaluation';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getNextOrder } from '@/utils/list';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import AssessmentForm, { AssessmentFormFields } from './AssessmentForm';

type CoursePathAssessmentFormServiceProps = {
  evaluation: Evaluation;
  coursePath: CoursePath;
  cacheManager: ListCacheManager<Evaluation>;
  assessment?: Assessment;
  onClose: () => void;
};

export default function CoursePathAssessmentFormService({
  evaluation,
  coursePath,
  cacheManager,
  assessment,
  onClose,
}: CoursePathAssessmentFormServiceProps) {
  const [createdAssessment, setCreatedAssessment] = useState<Assessment>();

  const evaluationFromState = cacheManager.getById(evaluation.id) ?? evaluation;
  const baseAssessment = assessment ?? createdAssessment;
  const assessmentFromState =
    evaluationFromState.assessments?.find(a => a.id === baseAssessment?.id) ??
    baseAssessment;

  const { createAssessment, updateAssessment } = useAssessments({
    evaluation: evaluationFromState,
    cacheManager,
  });

  const slaveEntries = useMemo(
    () => [{ slave: evaluationFromState, coursePath }],
    [evaluationFromState, coursePath],
  );

  const handleCreate = async (data: AssessmentFormFields) => {
    const lessonBlockIds = data.lessonBlocksBySlave[0]?.lessonBlockIds ?? [];
    const newAssessment = await createAssessment({
      name: data.name,
      description: data.description,
      maxGrade: data.maxGrade,
      order: getNextOrder(evaluationFromState.assessments ?? []),
      lessonBlockIds,
    });
    setCreatedAssessment(newAssessment);
  };

  const handleUpdate = async (data: AssessmentFormFields) => {
    if (!assessmentFromState)
      throw new Error('Assessment is required for update');

    const lessonBlockIds = data.lessonBlocksBySlave[0]?.lessonBlockIds ?? [];
    const dataChanges = buildChangedObject(assessmentFromState, {
      name: data.name,
      description: data.description,
      maxGrade: data.maxGrade,
      lessonBlockIds,
    });

    if (Object.keys(dataChanges).length > 0) {
      await updateAssessment({
        assessmentId: assessmentFromState.id,
        data: dataChanges,
      });
    }
  };

  const handleSubmit = async (data: AssessmentFormFields) => {
    if (!assessmentFromState) return handleCreate(data);
    else return handleUpdate(data);
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
  });

  return (
    <AssessmentForm
      evaluation={evaluationFromState}
      assessment={assessmentFromState}
      cacheManager={cacheManager}
      slaveEntries={slaveEntries}
      onSubmit={onSubmit}
      onCancel={onClose}
      isLoading={isPending}
    />
  );
}
